import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type ProfileEditWidgetProps,
  type ProfileEditThemingProps,
  PROFILE_EDIT_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionProfileEdit for imperative control.
 */
export interface LegionProfileEditHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildProfileEditWidgetUrl(
  props: ProfileEditWidgetProps
): string {
  const { baseUrl, theme = "light" } = props;

  const url = new URL("/widget/profile-edit", baseUrl);
  url.searchParams.set("theme", theme);

  // Append theming CSS variable params
  const themingKeys = Object.keys(
    PROFILE_EDIT_THEMING_PROP_TO_PARAM
  ) as Array<keyof ProfileEditThemingProps>;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(
        PROFILE_EDIT_THEMING_PROP_TO_PARAM[key],
        String(value)
      );
    }
  }

  return url.toString();
}

/**
 * LegionProfileEdit – a React component that embeds the Legion Profile Edit
 * widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionProfileEdit } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionProfileEdit
 *       baseUrl="https://your-legion-domain.com"
 *       authToken="your-jwt-token"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionProfileEdit = forwardRef<
  LegionProfileEditHandle,
  ProfileEditWidgetProps
>(function LegionProfileEdit(props, ref) {
  const {
    theme = "light",
    authToken,
    width = "600px",
    height = "800px",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildProfileEditWidgetUrl(props),
    [
      baseUrl,
      theme,
      ...Object.keys(PROFILE_EDIT_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    // Send auth token via postMessage
    const iframe = iframeRef.current;
    if (authToken && iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "AUTH_TOKEN", token: authToken },
        baseUrl
      );
    }
    onLoad?.({ widget: "profile-edit", theme: theme ?? "light" });
  }, [onLoad, theme, authToken, baseUrl]);

  const handleError = useCallback(() => {
    onError?.("Failed to load profile edit widget");
  }, [onError]);

  return (
    <div
      className={className}
      style={{ width, height, ...style }}
      data-testid="legion-profile-edit"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Profile Edit Widget"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        onLoad={handleLoad}
        onError={handleError}
        style={{ border: "none" }}
      />
    </div>
  );
});

